import React, { lazy, Suspense, useMemo, useState, useEffect, useRef } from "react"
import { Link } from "gatsby"
import { useIsVisible } from 'react-is-visible'

import {
  dataStore,
  pageStore,
  userStore
} from "state/store-zustand";

import { loadState, saveState } from '../../localStorage'
import { trackEvent } from '../../analytics'
import { fetchPlacesDetails } from "components/utils/testHelpers.js"
import { getVibeGradient } from 'vibemap-constants/dist/vibes'
import { handleEventRoute } from "components/utils/routeUtils"
import { placeToCard } from 'components/post/blocks/singCardsBlock/placesToCards.js'

const CardMenu = lazy(() => import("components/elements/cards/menu"))
import Chip from 'components/shared/chip/chip'
const Icon = lazy(() => import("components/elements/icons/iconCategory"))

import EventBadge, { htmlDateTime } from "components/shared/eventBadge"
import LazyBackground from "components/elements/background-image"
import StarIcon from '@mui/icons-material/StarRate';

import usePlaceholder from "components/elements/background-image/placeholder"
import Skeleton from "react-loading-skeleton"
import Truncate from 'react-truncate'
import { MdArrowForward } from '@react-icons/all-files/md/MdArrowForward'

import "./singCard.scss"

const ApiSingCard = ({
  title = null,
  linkToDetails = true,
  addToMap = true,
  onMap = true,
  newTab = false,
  onVisible,
  backgroundSize = 'large',
  hideSaved = false,
  size = 'base',
  style = 'basic',
  clearCache = false,
  showDescription = false,
  hasData = false,
  showCatLabel = true,
  showNote = true,
  addAspectRatio = false,
  ...props
}) => {

  const [placeCurrent, setPlaceCurrent] = useState(null)
  const [placeDetails, setPlaceDetails] = useState(null)

  // Simplify the place to card
  const [cardState, setCardState] = useState(props.cardState)
  useEffect(() => {
    // Check if the card state has changed
    const cardStateDep = JSON.stringify(props.cardState)
    if (cardStateDep !== JSON.stringify(cardState)) {
      setCardState(props.cardState)
    }
  }, [props.cardState])

  const mountedRef = useRef()
  const isVisible = useIsVisible(mountedRef)
  const [isShown, setIsShown] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [isMapped, setIsMapped] = useState(false)

  const [stats, setStats] = useState(null)

  const places = dataStore((state) => state.places)
  const events = dataStore((state) => state.events)
  const addToPlaces = dataStore((state) => state.addToPlaces)
  const setEventID = dataStore((state) => state.setEventID)
  const setPlaceID = dataStore((state) => state.setPlaceID)
  const setShowEditForm = dataStore((state) => state.setShowEditForm)
  const handleShowEditForm = (show, id, type = 'event') => {
    if (type == 'event') {
      setEventID(id)
    }
    else {
      setPlaceID(id)
    }

    setShowEditForm(show);
  }

  const handleNewData = (newData) => {
    console.log('handleNewData ', newData);
    const newCardState = { ...cardState, ...newData }
    setCardState(newCardState)
    //setCardState(newData)
    //setCardState(newData)
  }

  // TODO: pass more of this from parent?
  const compact = pageStore((state) => state.compact)
  const debug = pageStore((state) => state.debug)
  const embedded = pageStore((state) => state.embedded)
  const isMobile = pageStore((state) => state.isMobile)
  const isTablet = pageStore((state) => state.isTablet)
  const makeLinkFromTheme = pageStore((state) => state.makeLinkFromTheme)
  const pageHasMap = pageStore((state) => state.pageHasMap)
  const showCardMenu = pageStore((state) => state.showCardMenu)
  const showHeader = pageStore((state) => state.showHeader)
  const themeClass = pageStore((state) => state.themeClass)
  const themeName = pageStore((state) => state.themeName)
  const themeObject = pageStore((state) => state.themeObject)

  // Show a context menu on right-click
  const [contextMenu, setContextMenu] = React.useState(null)

  const handleMenuClose = React.useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleContextMenu = (event) => {
    event.preventDefault()
    //console.log('handleContextMenu ', event);
    setContextMenu(
      contextMenu === null
        ? {
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 6,
        }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
        // Other native context menus might behave different.
        // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
        null,
    )
  }

  // effect just for tracking mounted state
  useEffect(() => {
    mountedRef.current = true
    return () => {
      //mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (isVisible) {
      setIsShown(true)
      if (onVisible) onVisible()

      // After a timeout, set the card to be shown
      setTimeout(() => {
        if (isShown == false) setIsShown(true)
      }, 1500)
    } // One way; once
  }, [isVisible])


  useEffect(() => {
    if (cardState?.stats) {
      setStats(cardState.stats)
      //console.log(`DEBUG effect: Show ranking details `, cardState.stats);
    }
  }, [cardState?.stats])


  // With ID or card state, set UI or fetch data
  const cardStateDep = JSON.stringify(cardState)
  useEffect(() => {

    //console.log('sing card rendered uuid');
    const id = props.uuid ? props.uuid.trim() : null

    // TODO: detect if the card already has data and hasn't changed
    // Add a better field ot indicate if the card is already loaded
    const hasCardData = cardState && cardState?.title && cardState?.image != undefined
    const hasContext = place !== undefined && addToPlaces !== undefined
    const shouldAdd = hasCardData && hasContext && addToMap

    //console.log('DEBUG: shouldAdd ', shouldAdd, hasCardData, placeCurrent)
    if (hasCardData) {
      setIsLoaded(true)
    }

    if (shouldAdd) {
      // Don't fetch; but add to the map
      // Make sure it's a new object; not a reference
      if (placeCurrent && placeCurrent.properties) {
        placeCurrent.index = placeCurrent?.index
          ? placeCurrent.index
          : props.index

        // FIXME: Add to map object to fix infinte render
        addToPlaces(placeCurrent)
        //console.log('addCard with index ', cardState?.title, cardState?.index);
        setIsLoaded(true)
      }
    } else {
      // Load places from API
      let placeFound = places.find(place => (place.id == id))
      let hasFullPlace = placeFound && placeFound.properties
      if (hasFullPlace && placeCurrent == undefined) {
        setPlaceCurrent(placeFound)
        setCardState(placeToCard(placeFound))
      }
        setIsLoaded(true)
      let eventFound = !clearCache && events.find(event => (event.id == id))
      //console.log('placeFound ', id, placeFound, places);

      const shouldSearch = !hasFullPlace && !eventFound
      console.log('shouldSearch ', shouldSearch, placeFound, eventFound);

      if (shouldSearch) {
        //console.log('fetching place details ', id );
        fetchPlacesDetails(id, props.type, true)
          .then((response) => {
            if (!response) return false

            // TODO: Move the fetching and parsing to a reusable method.
            let category = response?.data?.properties?.categories ? response.data.properties.categories[0] : ""
            let subcategory = category

            const vibemap_images = response?.data?.properties
              ? response?.data?.properties?.vibemap_images.map((image) => image.original)
              : []

            if (response.data?.properties?.categories && response.data.properties.categories.length > 0) subcategory = response.data.properties.categories[0]
            if (response.data?.properties?.short_description) subcategory = response.data.properties.short_description

            // TODO: Make this
            let stateArray = placeToCard(response.data)

            // Update state and add to page context
            if (mountedRef.current) {
              //setPlaces([...places, response.data])
              setPlaceCurrent(response.data)
              setPlaceDetails(stateArray)
              setCardState(stateArray)
              setIsLoaded(true)
            }

            // TODO: Remove: this is handled in effect
            if (false && places && addToMap && addToPlaces) {
              // Make sure it's a new object; not a reference
              const index = props.cardState.index

              response.data.index = index
              response.data.note = props.cardState.note
              //console.log('!isMapped && pageHasMap ', !isMapped, pageHasMap);
              //console.log('DEBUG: shouldAdd ', shouldAdd, ' to map?', shouldAdd, !isMapped && pageHasMap)
              if (!isMapped && pageHasMap) {
                addToPlaces(response.data, stateArray)
                setIsMapped(true)
              }

              // The map needs to know that the last item was added
              //setMapUpdated(props.lastItem)
            }
          })
      }
    }

    return () => {
      mountedRef.current = false;
    }

  }, [props.uuid, cardStateDep])

  //console.log(`Card props and state `, props, cardState);
  const { isEvent, uuid } = props
  const route = isEvent ? 'events' : 'details'
  const id = cardState?.id ? cardState.id.trim() : ''


  const link_event = useMemo(() => handleEventRoute({
    eventCard: cardState,
    theme: themeName,
    shouldOpenURL: themeObject?.cards?.open_to_website
  }), [cardState, themeClass, themeObject?.cards?.open_to_website])

  const link_place = uuid
    ? `/places/${route}/${uuid.trim()}`
    : `/places/${route}/${id}`

  const link = isMobile || linkToDetails
    ? isEvent
      ? link_event
      : link_place
    : link_place


  const iconSize = '1.0em'
  const place = useMemo(() => Object.assign({}, cardState), [cardState])


  // Check if saved
  let savedPlaces = loadState('savedPlaces')
  if (!savedPlaces) savedPlaces = []

  const foundIndex = place ? savedPlaces.findIndex(obj => obj.id === place.id) : -1
  useEffect(() => {
    setIsSaved(foundIndex > -1)
  }, [foundIndex])

  const _toggleSaved = () => {
    setIsSaved(value => !value)
  }

  const _handleSave = (e, place) => {
    e.preventDefault()
    e.stopPropagation()
    _toggleSaved()

    if (!isSaved) {
      // TODO: Also add Google Analytics
      savedPlaces.push(place)
      trackEvent('Saved', place)
    } else {
      savedPlaces.splice(foundIndex, 1)
    }
    saveState(savedPlaces, 'savedPlaces')
  }

  const cardTitle = title ? title : cardState?.title
  const titleLength = cardTitle ? cardTitle.length : 0

  const hasCard = cardState && cardTitle
  //if (!hasCard) return <Skeleton />

  const titleTag = (
    <h4 className="title">
      <a href={link}>
        {cardTitle
          ? titleLength > 80
            ?
            <Truncate
              lines={3}
              width={isMobile ? 200 : 310}
              ellipsis="...">
              {cardTitle}
            </Truncate>
            : cardTitle
          // <span dangerouslySetInnerHTML={{ __html: cardState.title.length > 50 ? `${cardState.title.substring(0, 50)}&hellip;` : `${cardState.title.substring(0, 50)}` }} />
          : <Skeleton />
        }
      </a>
    </h4>
  )

  const isImage = cardState?.image && cardState?.image.length > 0
  const randomize = themeObject?.events?.randomizeImages || false
  let bgImage = isImage
    ? randomize
      ? cardState?.image[Math.floor(Math.random() * cardState?.image.length)]
      : cardState?.image[0]
    : usePlaceholder(isEvent)

  if (cardState?.placeCardImage) bgImage = cardState?.placeCardImage

  const vibe = cardState?.vibes && cardState?.vibes.length > 0
    ? cardState.vibes[0]
    : 'chill'

  const gradientStyle = isLoaded
    ? {}
    : { background: getVibeGradient(vibe)['gradient'] }

  const dateTime = cardState?.dateTime
  const start_date = cardState?.start_date
  const end_date = cardState?.end_date

  const iconStyle = {
    position: 'absolute',
    top: '30%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10
  }

  const iconCat = cardState?.category || isEvent ? 'events' : 'place'
  const iconCatSize = isMobile ? 30 : 40
  const icon = isImage == false
    ? <Suspense fallback={null}>
      <Icon
        category={iconCat}
        style={iconStyle}
        height={iconCatSize}
        width={iconCatSize} />
    </Suspense>
    : null


  const loadImg = bgImage || cardState.image && isShown || props?.index < 10
  const image = loadImg
    ? <LazyBackground
        key={bgImage}
        src={bgImage}
        place={place}
        handleSave={_handleSave}
        isShowSaved={props.hideSaved ? false : true}
        isImage={isImage}
        isSaved={isSaved}
        isVisible={isShown}
        isShow={true}
        addAspectRatio={addAspectRatio}
        style={gradientStyle}>
      </LazyBackground>
    : <div className='background-image'>
      <Skeleton />
    </div>

  const address = cardState?.address
  const descLength = cardState?.description ? cardState?.description.length : 0

  const description = <>
    {(cardState?.description || cardState?.note) &&
      <div className="description">
        {cardState?.note
          ? <span dangerouslySetInnerHTML={{
            __html: cardState?.note
          }} />
          : <Truncate lines={2} >
            {cardState?.description}
          </Truncate>
        }
      </div>
    }</>

  const slug = cardState?.slug
  const data_source_url = place?.properties?.data_source_url
  const is_approved = cardState?.is_approved

  const note = cardState?.note
    ? <span className="note" dangerouslySetInnerHTML={{
      __html: cardState?.note
    }} />
    : null

  const showPrice = cardState && cardState?.card_type == 'event' || ['hotel', 'stay', 'visit'].some(cat => cardState?.categories && cardState?.categories?.includes(cat))
  //console.log(`Show prices `, showPrice, cardState);

  const isFree = typeof cardState?.price == "string" && cardState?.price.toUpperCase() == 'FREE'
  const is_featured = cardState?.is_featured

  const priceLevels = ['1', '2', '3', '4', '5']
  const isPriceLevel = !isEvent && cardState?.price && priceLevels.includes(cardState?.price?.toString())

  const price = showPrice && cardState?.price
    ? <strong className={`price`}>
      <span>
        {isFree ? '' : `$`}
        {isPriceLevel ? '$'.repeat(cardState?.price) : cardState?.price.toFixed(2)}
      </span>
    </strong>
    : null

  const category = (
    <span className="category">
      {" "}
      {cardState?.subcategory && cardState?.card_type !== "event" ? (
        <span dangerouslySetInnerHTML={{ __html: cardState?.subcategory, }} />
      ) : (
        <span>{cardState?.category ? cardState?.category : cardState?.card_type}</span>
      )}
    </span>
  )

  const vibes = (cardState?.vibes && cardState?.vibes.length > 0) && (
    <div className="vibes">
      {cardState?.vibes.slice(0, 3).map((vibe, i) => {
        return (
          <Chip
            key={`${i}_${vibe}`}
            size={'small'}
            text={vibe} />
        )
      })}
    </div>
  )

  const viewInMap = (props.hasMapLink && props.style !== 'flat') && (
    <span className="view-in-map">
      View
      <MdArrowForward
        title='Click to save to your list'
        size={iconSize}
      />
    </span>
  )

  const handleLink = (link, event, newTab = false, copyParams = false) => {
    event.preventDefault()
    let basicInfo = {}

    try {
      const { name, address, slug, url } = place.properties
      const coordinates = place?.geometry?.coordinates
      basicInfo = {
        name,
        address,
        slug,
        url,
        latitude: coordinates[1],
        longitude: coordinates[0]
      }
    } catch (error) {
      console.log('Problems with ', error);
    }

    const params = new URLSearchParams(basicInfo).toString();

    const linkWithInfo = copyParams && params ? `${link}?${params}` : link;
    if (window || newTab) {
      window.open(linkWithInfo, '_blank');
    }
  }

  // Only render when visible or previously shown
  //if (!isShown) return <Skeleton />
  const cardInnerClass = `sing-card-inner ${compact ? 'compact' : ''}`

  const CardLink = ({ link, children, ...props }) => {

    const open_to_website = themeObject?.cards?.open_to_website
    const open_to_blank = themeObject?.cards?.open_to_blank

    const linkNew = embedded
      ? makeLinkFromTheme({
        link: link,
        slug: slug,
        data_source_url: data_source_url,
        copyParams: themeObject?.cards?.open_with_params,
      })
      : link

    // Handle link when embeded
    return embedded && !open_to_website
      ? <div
          className={cardInnerClass}
          onClick={(event) => handleLink(linkNew, event)}>
          {children}
      </div>
      : <a
          className={cardInnerClass}
            href={linkNew}
            target={newTab
              ? '_blank'
              : embedded
                ? '_parent'
                : '_self'}
            state={{ place: cardState }} >
          {children}
        </a>
  }

  const dateInline = compact ? htmlDateTime(dateTime) : null
  const showAddress = address && props.showAddress || compact == 'true'

  const showEventMenu = cardState?.card_type == 'event'
  const showPlaceMenu = cardState?.card_type == 'place'

  const showBadge = props.isEvent && compact == false
  const isLoggedIn = userStore((state) => state.isLoggedIn)
  const user = userStore((state) => state.user)

  const Badge = () => {
    if (is_featured) {
      return (
        <span className="featured-badge">
          <StarIcon sx={{ fontSize: 16, marginRight: '4px' }} />
          Featured
        </span>
      )
    } else if (isFree) {
      return (
        <span className="featured-badge free">
          Free
        </span>
      )
    } else if (is_approved == false) {
      return (
        <span className="featured-badge pending">
          Not Approved
        </span>
      )
    }
    else {
      return null
    }

  }


  const cardLayoutVertical = (
    <>
      <div>
        {showBadge
          ? <EventBadge
            dateTime={dateTime}
            end_date={end_date} />
          : null}
        <Badge />
        {icon}
        {image}
      </div>
      <div className="sing-card-bottom">
        {showCatLabel ? category : nul}
        {viewInMap}
        {price}
        {titleTag}
        {showAddress ? address : null}
        {props.showDescription && description}
        {props.showNote && note}

        {vibes}
      </div>

      {isLoggedIn
        ? <Suspense fallback={null}>
          <CardMenu
            contextMenu={contextMenu}
            handleMenuClose={handleMenuClose}
            handleNewData={handleNewData}
            card={cardState}
            primaryAction={is_approved == true ? 'disapprove' : 'approve' }
            secondaryAction={is_approved == true ? 'approve' : 'disapprove' }
            primaryActionText={is_approved == true ? '🚫 Disapprove' : '✅ Approve'}
            secondaryActionText={is_approved == true ? '✅' : '🚫'}
            showButton={showCardMenu}
            showEditForm={handleShowEditForm}
            showPlaceMenu={showPlaceMenu}
            showEventMenu={showEventMenu}
            user={user}
          />
        </Suspense>
        : null
      }

    </>
  )

  const column_1 = compact ? 'is-2' : 'is-4'
  const column_2 = compact ? 'is-10' : 'is-8'

  const cardLayoutHorizontal = (
    <div className="columns is-flex-tablet is-gapless">

      {compact == false
        ? (
          <div className={`column ${column_1} image is-flex-tablet`}>
            {props.isEvent
              ? <EventBadge dateTime={dateTime} />
              : null
            }
            {image}
          </div>
        )
        : null
      }

      <div className={`column ${column_2} text is-flex-tablet`}>
        <div className="inner-column sing-card-bottom">
          {compact ? <small>{category}</small> : category}
          {titleTag}
          {showAddress ? address : null}
          {props.showDescription && description}
          {price}
          {vibes}
          {props.hasMapLink && (
            <span className="view-in-map">View in the map</span>
          )}
        </div>
      </div>
      {isLoggedIn
        ? <Suspense fallback={null}>
          <CardMenu
            contextMenu={contextMenu}
            handleMenuClose={handleMenuClose}
            showPlaceMenu={showPlaceMenu}
            showEventMenu={showEventMenu}
            showEditForm={handleShowEditForm}
            card={cardState} />
        </Suspense>
        : null
      }

    </div>
  )

  const card = <CardLink key={link} link={link}>
    {props.orientation == 'vertical'
      ? cardLayoutVertical
      : cardLayoutHorizontal
    }
  </CardLink>

  const statsTable = debug && stats
    ? Object.entries(stats).map(item => {
      return <div className={`stats`}><span>{item[0]}: </span><span>{Math.round(item[1]).toFixed(2)}</span></div>
    })
    : null

  const cardClassname = `sing-card o-${props.orientation} s-${props.style || ''} ${props.size || ''} ${is_featured ? 'featured' : ''}`

  return (
    <article
      id={id}
      key={id}
      ref={mountedRef}
      className={cardClassname}
      style={{ opacity: is_approved || !isLoggedIn ? 1 : 0.7 }}>
      {card}
      {statsTable}
    </article>
  )
}

//ApiSingCard.whyDidYouRender = true
export default ApiSingCard